import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  tabsContainer: {
    width: "100%",
    position: "relative",
    backgroundColor: "transparent",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    "&.dark-mode": {
      borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    },
  },
  tabsRoot: {
    minHeight: "42px",
    backgroundColor: "transparent",
    "& .MuiTabs-flexContainer": {
      gap: "0px",
    },
    "& .MuiTabs-scroller": {
      backgroundColor: "transparent",
    },
  },
  tabRoot: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "24px",
    letterSpacing: "0.4px",
    textTransform: "none !important",
    minHeight: "42px",
    padding: "9px 16px",
    color: "rgba(0, 0, 0, 0.6)",
    minWidth: "auto",
    transition: "color 0.2s ease",
    backgroundColor: "transparent",
    "&.Mui-selected": {
      color: "#007bff",
      fontWeight: 500,
    },
    "&:hover": {
      opacity: 0.8,
    },
  },
  tabRootDark: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "24px",
    letterSpacing: "0.4px",
    textTransform: "none !important",
    minHeight: "42px",
    padding: "9px 16px",
    color: "rgba(255, 255, 255, 0.6) !important",
    minWidth: "auto",
    transition: "color 0.2s ease",
    backgroundColor: "transparent",
    "&.Mui-selected": {
      color: "#007bff !important",
      fontWeight: 500,
    },
    "&:hover": {
      opacity: 0.8,
    },
  },
  indicator: {
    backgroundColor: "#007bff",
    height: "2px",
    bottom: 0,
  },
  endText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0.46px",
    color: "rgba(0, 0, 0, 0.6)",
    whiteSpace: "nowrap",
    paddingRight: "24px",
  },
  endTextDark: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0.46px",
    color: "rgba(255, 255, 255, 0.6)",
    whiteSpace: "nowrap",
    paddingRight: "24px",
  },
}));

/**
 * ReusableTabs Component
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with { label, value, path? }
 * @param {string|number} props.value - Current active tab value
 * @param {function} props.onChange - Callback when tab changes (receives event, newValue)
 * @param {boolean} props.useUrlNavigation - Enable URL navigation (default: false)
 * @param {boolean} props.darkMode - Enable dark mode styling (default: false)
 * @param {string} props.endText - Optional text to display at the end of tabs (default: "")
 * @param {Object} props.sx - Additional sx props for the Box container
 * @param {Object} props.tabsProps - Additional props to pass to Tabs component
 *
 * @example
 * // Basic usage without URL navigation
 * <ReusableTabs
 *   tabs={[
 *     { label: "Live Alerts", value: 0 },
 *     { label: "Past Alerts", value: 1 }
 *   ]}
 *   value={activeTab}
 *   onChange={(e, newValue) => setActiveTab(newValue)}
 * />
 *
 * @example
 * // With URL navigation and end text
 * <ReusableTabs
 *   tabs={[
 *     { label: "Live Alerts", value: "live", path: "/ai-alerts/live" },
 *     { label: "Past Alerts", value: "past", path: "/ai-alerts/past" }
 *   ]}
 *   value={currentTab}
 *   useUrlNavigation={true}
 *   endText="Last updated: Thursday, Nov 28, 2024 at 1:23 PM (EST)"
 * />
 */
function ReusableTabs({
  className = "",
  tabs = [],
  value,
  onChange,
  useUrlNavigation = false,
  darkMode = false,
  endText = "",
  sx = {},
  tabsProps = {},
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  // Determine active tab based on URL if useUrlNavigation is enabled
  const getActiveValue = () => {
    if (useUrlNavigation) {
      const currentPath = location.pathname;
      const matchedTab = tabs.find((tab) => tab.path === currentPath);
      return matchedTab ? matchedTab.value : value;
    }
    return value;
  };

  const activeValue = useUrlNavigation ? getActiveValue() : value;

  const handleChange = (event, newValue) => {
    // If URL navigation is enabled, navigate to the corresponding path
    if (useUrlNavigation) {
      const selectedTab = tabs.find((tab) => tab.value === newValue);
      if (selectedTab && selectedTab.path) {
        history.push(selectedTab.path);
      }
    }

    // Call the onChange callback if provided
    if (onChange) {
      onChange(event, newValue);
    }
  };

  return (
    <Box
      className={clsx(
        classes.tabsContainer,
        darkMode && "dark-mode",
        className,
      )}
      sx={{
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          justifyContent: "space-between",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Tabs
          className={classes.tabsRoot}
          TabIndicatorProps={{
            className: classes.indicator,
          }}
          value={activeValue}
          onChange={handleChange}
          {...tabsProps}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.value || index}
              label={tab.label}
              value={tab.value}
              className={darkMode ? classes.tabRootDark : classes.tabRoot}
              disabled={tab.disabled || false}
            />
          ))}
        </Tabs>
        {endText && (
          <p className={darkMode ? classes.endTextDark : classes.endText}>
            {endText}
          </p>
        )}
      </Box>
    </Box>
  );
}

export default ReusableTabs;
